import React, { useContext, useEffect, useState } from 'react';

import { MobXProviderContext, observer } from 'mobx-react';

import { Button, Icon, Loader, Table } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/UserInstitutions.less';

import { SatCoreRegister } from '../SatCoreRegistry';

import { DEFAULT_USER_INSTITUTION_CELLS, USER_INSTITUTION_CELL } from '../constants/UserInstitutionsConstants';

import { DIALOG_NAMES } from '../managers/DialogManager';

import PopupService from '../services/PopupService';
import UserService from '../services/UserService';

/**
 * **UserInstitutions** renders a tabled list of institutions (schools/districts)
 * a user belongs to, with add/remove when applicable. Embeddable in any profile
 * surface (e.g., Profile, EditStudentProfile, AdminUserProfile).
 */
const UserInstitutions = observer((props = {}) => {
  const {
    allowedCells = DEFAULT_USER_INSTITUTION_CELLS,
    onChange,
    primaryInstitutionId = null,
    readOnly = false,
    searchType = 'school',
    t,
    userId
  } = props;

  const translationKeySuffix = searchType === 'institution' ? 'Institution' : 'School';

  const { adminUsersManager, dialogManager, userManager } = useContext(MobXProviderContext);

  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUserInstitutions = async () => {
    const data = await UserService.fetchUserInstitutions(null, { userId });
    const list = data || [];

    // keep the relevant manager-level institutions cache in sync with what
    // we just fetched, so other consumers (e.g., UserInstitutionDropdown,
    // header/sidebar institution lists) reflect the change immediately
    const isLoggedInUser = userId === userManager.userId;
    const isAdminSelectedUser = userId === adminUsersManager.currentUser?.id;
    if (isLoggedInUser) {
      userManager.replaceUserInstitutionsMap(list);
    } else if (isAdminSelectedUser) {
      adminUsersManager.replaceInstitutionsForSelectedUserMap(list);
    }

    setInstitutions(list);
    return {
      primaryInstitutionChanged: !list.some((institution) => {
        return institution?.id && institution.id === primaryInstitutionId;
      })
    };
  };

  useEffect(() => {
    if (userId) {
      (async () => {
        setLoading(true);
        await fetchUserInstitutions();
        setLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const refetch = async () => {
    setLoading(true);
    const { primaryInstitutionChanged } = await fetchUserInstitutions();
    await onChange?.({ primaryInstitutionChanged });
    setLoading(false);
  };

  // prefer the host's named primary; fall back to any remaining institution
  // when the named primary is missing from the list (e.g. host has a stale
  // `primaryInstitutionId` because the user has not been re-fetched yet).
  // All satellite-scope schools share a district, so any institution's
  // `parentId` yields a valid `districtId`.
  const primary = institutions.find((institution) => institution.id === primaryInstitutionId)
    || institutions[0];
  let primaryDistrictId = null;
  if (primary) {
    primaryDistrictId = primary.type === 'District' ? primary.id : primary.parentId;
  }

  // satellite is limited to school scope for now -- hide district institutions from the table
  const visibleInstitutions = searchType === 'school'
    ? institutions.filter((institution) => institution.type !== 'District')
    : institutions;

  const openAddInstitutionModal = () => {
    dialogManager.setOpenDialog(DIALOG_NAMES.ADD_USER_INSTITUTION_MODAL, {
      districtId: primaryDistrictId,
      existingUserInstitutions: institutions,
      onInstitutionAdded: refetch,
      searchType,
      userId
    }, () => dialogManager.closeDialog(DIALOG_NAMES.ADD_USER_INSTITUTION_MODAL));
  };

  const openRemoveConfirm = (row) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.CONFIRM, {
      cancelButtonClass: 'cancelButton',
      cancelButtonName: t('removeCancelButton'),
      confirmButtonClass: 'saveButton',
      confirmButtonName: t('removeConfirmButton'),
      confirmHandler: async () => {
        await UserService.removeUserInstitution({ institutionId: row.id, userId });
        await refetch();
        dialogManager.closeDialog(DIALOG_NAMES.CONFIRM);
      },
      message: t(`removeConfirmMsg${translationKeySuffix}`, { name: row.name }),
      title: t(`removeConfirmTitle${translationKeySuffix}`)
    }, () => dialogManager.closeDialog(DIALOG_NAMES.CONFIRM));
  };

  // when only one institution remains, the trash icon is shown but disabled
  // with a tooltip; the column itself is hidden when 'remove' is not in `allowedCells`
  const isOnlyInstitution = institutions.length <= 1;

  const renderInstitutionHeaderCell = ({ id, className, value }) => (
    <Table.HeaderCell key={id} className={classNames(id, className)}>
      {value}
    </Table.HeaderCell>
  );

  const renderInstitutionBodyCell = ({ id, className, value }) => (
    <Table.Cell key={id} className={classNames(id, className)}>
      <span title={value}>{value}</span>
    </Table.Cell>
  );

  const renderRemoveBodyCell = (row) => {
    const trigger = isOnlyInstitution
      ? <span className='cell-remove-notApplicable'>{t('removeNotApplicable')}</span>
      : (
        <Icon
          aria-label={t('headerRemove')}
          link
          name='trash'
          onClick={() => openRemoveConfirm(row)} />
      );
    return (
      <Table.Cell key='cell-remove' className='cell-remove'>
        {isOnlyInstitution
          ? PopupService.renderPopup({
            content: t(`removeDisabledMsg${translationKeySuffix}`),
            position: 'top center',
            trigger,
            wide: false
          })
          : trigger}
      </Table.Cell>
    );
  };

  const renderInstitutionHeader = () => (
    <Table.Header>
      <Table.Row>
        {allowedCells.map((cellName) => {
          switch (cellName) {
            case USER_INSTITUTION_CELL.INSTITUTION:
              return renderInstitutionHeaderCell({ id: 'cell-institution', value: t(`header${translationKeySuffix}`) });
            case USER_INSTITUTION_CELL.PARENT_DISTRICT:
              return renderInstitutionHeaderCell({ id: 'cell-parent-district', value: t('headerParentDistrict') });
            case USER_INSTITUTION_CELL.CITY:
              return renderInstitutionHeaderCell({ id: 'cell-city', value: t('headerCity') });
            case USER_INSTITUTION_CELL.STATE:
              return renderInstitutionHeaderCell({ id: 'cell-state', value: t('headerState') });
            case USER_INSTITUTION_CELL.COUNTRY:
              return renderInstitutionHeaderCell({ id: 'cell-country', value: t('headerCountry') });
            case USER_INSTITUTION_CELL.REMOVE:
              if (readOnly) {
                return null;
              }
              return renderInstitutionHeaderCell({ id: 'cell-remove', value: t('headerRemove') });
            default:
              return null;
          }
        })}
      </Table.Row>
    </Table.Header>
  );

  const renderInstitutionRow = (row) => (
    <Table.Row key={row.id}>
      {allowedCells.map((cellName) => {
        switch (cellName) {
          case USER_INSTITUTION_CELL.INSTITUTION:
            return renderInstitutionBodyCell({ id: 'cell-institution', value: row.name });
          case USER_INSTITUTION_CELL.PARENT_DISTRICT:
            return renderInstitutionBodyCell({ id: 'cell-parent-district', value: row.districtName });
          case USER_INSTITUTION_CELL.CITY:
            return renderInstitutionBodyCell({ id: 'cell-city', value: row.city });
          case USER_INSTITUTION_CELL.STATE:
            return renderInstitutionBodyCell({ id: 'cell-state', value: row.stateId });
          case USER_INSTITUTION_CELL.COUNTRY:
            return renderInstitutionBodyCell({ id: 'cell-country', value: row.countryId });
          case USER_INSTITUTION_CELL.REMOVE:
            if (readOnly) {
              return null;
            }
            return renderRemoveBodyCell(row);
          default:
            return null;
        }
      })}
    </Table.Row>
  );

  return (
    <div className='UserInstitutions'>
      <div className='user-institutions-header'>
        <div className='user-institutions-title'>{t(`sectionTitle${translationKeySuffix}`)}</div>
        {!readOnly && (
          <Button
            className='user-institutions-add-button'
            disabled={loading}
            onClick={openAddInstitutionModal}
            primary>
            {t(`addButton${translationKeySuffix}`)}
          </Button>
        )}
      </div>
      {loading ? (
        <Loader active inline='centered' />
      ) : (
        <Table className={classNames('user-institutions-table', { readOnly })}>
          {renderInstitutionHeader()}
          <Table.Body>
            {visibleInstitutions.map(renderInstitutionRow)}
          </Table.Body>
        </Table>
      )}
    </div>
  );
});

export default UserInstitutions;

SatCoreRegister('UserInstitutions', UserInstitutions);
